import { Injectable } from '@angular/core';
import { CanDeactivate } from '@angular/router';
import { Observable } from 'rxjs';
import { EditSessionRegistry } from '../shared/edit-session/edit-session-registry.service';
import { DirtyCheckable } from '../shared/edit-session/edit-session';
import { ConfirmationDialogService } from '../shared/ui/confirmation-dialog/confirmation-dialog.service';

/**
 * A page that wants the prompt can implement this instead of registering a
 * session — a hand-rolled `get dirty()` (as `work-item-detail.component.ts` has)
 * or a reactive form is just as good an answer to the question.
 */
export type HasUnsavedChanges = DirtyCheckable;

/**
 * Blocks navigation away from a page holding unsaved edits until the user says
 * to discard them.
 *
 * Two sources, either of which is enough:
 *  - the deactivating component itself exposes `dirty`;
 *  - any `EditSession` in `EditSessionRegistry` is dirty — which covers a page
 *    that hands its session to a child component and has no `dirty` of its own.
 *
 * Class-based `CanDeactivate` with `providedIn: 'root'`, matching `AuthGuard`
 * and `PermissionGuard`; wired the same way, in the route's `canDeactivate`.
 *
 * In-app navigation uses the shared application dialog. A browser refresh or
 * tab close still uses `beforeunload`, whose prompt is controlled by the browser.
 */
@Injectable({ providedIn: 'root' })
export class UnsavedChangesGuard implements CanDeactivate<unknown> {
  constructor(
    private readonly registry: EditSessionRegistry,
    private readonly confirmation: ConfirmationDialogService,
  ) {}

  canDeactivate(component: unknown): boolean | Observable<boolean> {
    if (!this.isDirty(component)) return true;
    return this.confirmation.confirm({
      title: 'Discard changes?',
      message: 'Your unsaved changes will be lost.',
      confirmLabel: 'Discard changes',
      cancelLabel: 'Keep editing',
      danger: true,
    });
  }

  private isDirty(component: unknown): boolean {
    const own = !!component
      && typeof component === 'object'
      && 'dirty' in component
      && !!(component as DirtyCheckable).dirty;
    return own || this.registry.dirty;
  }
}
